import React, { useCallback, useMemo } from 'react';

import { TFunction } from '@lyearn/i18n';
import { Calendar2Outline, Close } from '@lyearn/icons';
import {
  ActionMenu,
  ActionMenuItemType,
  ActionMenuOverrides,
  ActionMenuProps,
} from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { InfoPill, LinkButton, Stack, Tooltip } from '@lyearn/ui';
import {
  AssignmentDueDateEnum,
  ContentCollectionGroupType,
  ContentEntity,
  DueDateAssignmentConfig,
  DueDateNestingLevelEnum,
  EventUnion,
  ResourceTypeEnum,
} from '@/types/schema';

import { RenderItemActionsProps } from '../types';
import { getCollectionMenuItems, getPathMenuItems, getSelectedDueDateLabel } from '../utils';

const ACTION_MENU_OVERRIDES: ActionMenuOverrides = { PopoverContent: { position: 'bottom-start' } };
const MAPPED_NESTING_LABEL = {
  [DueDateNestingLevelEnum.Contents]: 'AssignmentCreator:ContentDueDate',
  [DueDateNestingLevelEnum.Sections]: 'AssignmentCreator:SectionDueDate',
};
const TYPOGRAPHY_VARIANT = {
  variant: 'heading-xs' as const,
};

const renderActions = ({
  actionMenu,
  disabled,
  onSetDueDate,
  onDelete,
  t,
  dueDateConfig,
  nestingLevel,
}: {
  actionMenu?: {
    items: ActionMenuProps['items'];
    onAction: (actionType: string, item: ActionMenuItemType) => void;
  };
  disabled?: boolean;
  onSetDueDate: () => void;
  onDelete: () => void;
  t: TFunction;
  dueDateConfig?: DueDateAssignmentConfig | null;
  nestingLevel?: DueDateNestingLevelEnum | null;
}) => {
  let DueDateComp;

  if (nestingLevel) {
    const label = t(MAPPED_NESTING_LABEL[nestingLevel]);

    DueDateComp = (
      <InfoPill
        appearance="secondary"
        label={label}
        shape="square"
        type="text"
        variant="filled"
        onClick={onSetDueDate}
      />
    );
  } else {
    DueDateComp = dueDateConfig ? (
      <RenderDueDate selectedDueDate={dueDateConfig} t={t} onClick={onSetDueDate} />
    ) : actionMenu ? (
      <ActionMenu
        items={actionMenu.items}
        overrides={ACTION_MENU_OVERRIDES}
        onAction={actionMenu.onAction}>
        <Tooltip placement="top" title={`${t('AssignmentCreator:SetDueDate')}`}>
          <LinkButton
            className={cn('py-2 px-4', 'flex-shrink-0 transition duration-150 ease-linear', {
              'opacity-100': disabled,
              'opacity-0 group-hover:opacity-100 ': !disabled,
            })}
            color={'internal'}>
            <Calendar2Outline />
          </LinkButton>
        </Tooltip>
      </ActionMenu>
    ) : (
      <Tooltip placement="top" title={`${t('AssignmentCreator:SetDueDate')}`}>
        <LinkButton
          className={cn('py-2 px-4', 'flex-shrink-0 transition duration-150 ease-linear', {
            'opacity-100': disabled,
            'opacity-0 group-hover:opacity-100 ': !disabled,
          })}
          color={'internal'}
          onClick={onSetDueDate}>
          <Calendar2Outline />
        </LinkButton>
      </Tooltip>
    );
  }

  return (
    <Stack alignItems="center">
      {DueDateComp}
      <Tooltip placement="top" title={`${t('AssignmentCreator:RemoveContent')}`}>
        <LinkButton
          className="flex-shrink-0 py-2 px-4 opacity-0 transition duration-150 ease-linear group-hover:opacity-100"
          color={'internal'}
          disabled={disabled}
          onClick={onDelete}>
          <Close />
        </LinkButton>
      </Tooltip>
    </Stack>
  );
};

export const RenderContentActions = ({
  disabled,
  onDelete,
  onSetDueDate,
  t,
  item: content,
  assignmentItemsDueDateConfig,
  setNestedDialogState,
}: RenderItemActionsProps<ContentEntity>) => {
  const contentDueDateConfig = assignmentItemsDueDateConfig[content.id];
  const nestingLevel = contentDueDateConfig?.dueDateNestingLevel;
  const dueDateConfig = contentDueDateConfig?.dueDateConfig;
  const nestedDueDateConfig = contentDueDateConfig?.nestedDueDateConfig;

  const _onSetDueDate = useCallback(() => {
    switch (nestingLevel) {
      case DueDateNestingLevelEnum.Sections:
        setNestedDialogState?.(nestedDueDateConfig);
        onSetDueDate(content, DueDateNestingLevelEnum.Sections);
        break;
      case DueDateNestingLevelEnum.Contents:
        setNestedDialogState?.(nestedDueDateConfig);
        onSetDueDate(content, DueDateNestingLevelEnum.Contents);
        break;
      default:
        onSetDueDate(content);
    }
  }, [content, nestedDueDateConfig, nestingLevel, onSetDueDate, setNestedDialogState]);

  const onMenuItemClick = useCallback(
    (item: string) => {
      switch (item) {
        case 'path':
          _onSetDueDate();

          return;
        case 'section':
          onSetDueDate(content, DueDateNestingLevelEnum.Sections);

          return;
        case 'content':
          onSetDueDate(content, DueDateNestingLevelEnum.Contents);
      }
    },
    [_onSetDueDate, content, onSetDueDate],
  );

  const actionMenu = useMemo(() => {
    if (content.type === ResourceTypeEnum.Path) {
      return {
        items: getPathMenuItems(t),
        onAction: onMenuItemClick,
      };
    }
  }, [content.type, t, onMenuItemClick]);

  return renderActions({
    actionMenu,
    disabled,
    onDelete,
    onSetDueDate: _onSetDueDate,
    t,
    dueDateConfig,
    nestingLevel,
  });
};

export const RenderCollectionActions = ({
  disabled,
  onDelete,
  onSetDueDate,
  t,
  item: collection,
  setNestedDialogState,
  assignmentItemsDueDateConfig,
}: RenderItemActionsProps<ContentCollectionGroupType>) => {
  const contentDueDateConfig = assignmentItemsDueDateConfig[collection.id];
  const nestingLevel = contentDueDateConfig?.dueDateNestingLevel;
  const dueDateConfig = contentDueDateConfig?.dueDateConfig;
  const nestedDueDateConfig = contentDueDateConfig?.nestedDueDateConfig;

  const _onSetDueDate = useCallback(() => {
    switch (nestingLevel) {
      case DueDateNestingLevelEnum.Contents:
        setNestedDialogState?.(nestedDueDateConfig);
        onSetDueDate(collection, DueDateNestingLevelEnum.Contents);
        break;
      default:
        onSetDueDate(collection);
    }
  }, [collection, nestedDueDateConfig, nestingLevel, onSetDueDate, setNestedDialogState]);

  const onMenuItemClick = useCallback(
    (item: string) => {
      switch (item) {
        case 'collection':
          _onSetDueDate();

          return;
        case 'content':
          onSetDueDate(collection, DueDateNestingLevelEnum.Contents);
      }
    },
    [_onSetDueDate, collection, onSetDueDate],
  );

  const actionMenu = {
    items: getCollectionMenuItems(t),
    onAction: onMenuItemClick,
  };

  return renderActions({
    actionMenu,
    disabled,
    onDelete,
    onSetDueDate: _onSetDueDate,
    t,
    dueDateConfig,
    nestingLevel,
  });
};

export const RenderSessionActions = ({
  disabled,
  onDelete,
  onSetDueDate,
  t,
  item: session,
  assignmentItemsDueDateConfig,
}: RenderItemActionsProps<EventUnion>) => {
  const contentDueDateConfig = assignmentItemsDueDateConfig[session.id];
  const dueDateConfig = contentDueDateConfig?.dueDateConfig;

  return renderActions({
    disabled,
    onDelete,
    onSetDueDate: () => onSetDueDate(session),
    t,
    dueDateConfig,
  });
};

export const RenderDueDate = ({
  t,
  onClick,
  selectedDueDate,
}: {
  t: TFunction;
  onClick: () => void;
  selectedDueDate?: DueDateAssignmentConfig | null;
}) => {
  const dueDateLabel = useMemo(
    () => getSelectedDueDateLabel(t, selectedDueDate),
    [selectedDueDate, t],
  );

  if (!selectedDueDate) {
    return (
      <LinkButton
        className="flex-shrink-0 py-2 px-4"
        color={'internal'}
        startIcon={<Calendar2Outline />}
        onClick={onClick}>
        {t('AssignmentCreator:SetDueDate')}
      </LinkButton>
    );
  }

  const PillComp = (
    <InfoPill
      appearance="secondary"
      label={dueDateLabel}
      shape="square"
      type="text"
      variant="filled"
      onClick={onClick}
    />
  );

  if (selectedDueDate.dueDateType === AssignmentDueDateEnum.Fixed) {
    return <Tooltip title={dueDateLabel!}>{PillComp}</Tooltip>;
  }

  return PillComp;
};

export const RenderRemoveDueDate = (t: TFunction, onClick: () => void) => {
  return (
    <LinkButton
      className="py-2 px-4"
      color={'internal'}
      typography={TYPOGRAPHY_VARIANT}
      onClick={onClick}>
      {t('AssignmentCreator:RemoveDueDate')}
    </LinkButton>
  );
};
